import type { Schemas } from '@npc/api-client'
import { useState } from 'react'

import { useSetMemberRole } from '../../app/queries'
import { ApiFailure } from '../../app/errors'
import { Modal } from '../../components/modal'
import './team.css'
import { StepUpDialog } from '../../components/step-up'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { ProjectPicker } from './project-picker'

/**
 * 「改他的身份」那张表：身份、能碰哪些项目 → 当场再验一次 → 保存。
 *
 * 与邀请表（invite-dialog）是同一张脸、同一套规矩：
 *
 * **一、改人是改团队权限**（CLAUDE.md §10 点名的四件之一），中间夹一道
 * StepUpDialog。资源串是**这个人的 user_id**——与服务端
 * `auth.PathResource("userId")` 一字不差：为谁验的就只能改谁。
 *
 * **二、身份和范围一起交**。后端 `setMemberRequest` 里两个指针本来支持
 * 只改一样，但「先升身份、回头再补项目」的中间态里，那个人拿着新身份
 * 却什么都打不开——界面上不让走出这种半截状态。
 *
 * **三、至少留一个老板**是服务端的规矩（roster 的 guardLastOwnerLocked），
 * 这里不预判、不灰掉老板那一行——UI 抢先算一遍就会有两份权限表，
 * 而契约点名只要一份。
 */
export function MemberDialog({
  open,
  member,
  roles,
  projects,
  onClose,
}: {
  open: boolean
  member: Schemas['Member'] | null
  roles: readonly Schemas['MemberRoleInfo'][]
  projects: readonly Schemas['Project'][]
  onClose: () => void
}) {
  const patchMember = useSetMemberRole()
  /**
   * 表里预填的范围。
   *
   * **`global` 为真时不能填空。** 契约里 `global` 的人 `projects` 是空数组，
   * 而名单那一列（`Row` 里的 `scope`）照 `global` 的定义把所有项目都列出来。
   * 预填直接用 `member.projects` 的话，同一屏上两处对同一个人说了两句相反的话：
   * 列表说「他碰得到这九个」，点开「改」说「一个都没挑」。
   *
   * 而它不只是难看——`applyMemberPatch` 里写着「显式项目列表就是显式收窄」，
   * 传 `projects` 会把 `all_projects` 置为 false。也就是说：一个只想改身份的人，
   * 点开、随手勾一个、保存，**当场把这个人从「所有项目」收窄成那一个**，
   * 而界面从头到尾没说过这件事。
   *
   * 所以预填与名单那一列取同一个事实：`global` 就是全部。
   * 这样「什么都不动直接保存」保住的是他现在看得见的东西。
   */
  const initialPicked = member?.global ? projects.map((one) => one.id) : (member?.projects ?? [])
  const [role, setRole] = useState<Schemas['MemberRole']>(member?.role ?? 'viewer')
  const [picked, setPicked] = useState<readonly string[]>(initialPicked)
  const [problem, setProblem] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [saved, setSaved] = useState(false)

  const needsProjects = role !== 'owner'
  const roleSummary = roles.find((item) => item.role === role)?.summary

  function reset() {
    setRole(member?.role ?? 'viewer')
    setPicked(initialPicked)
    setProblem(null)
    setVerifying(false)
    setSaved(false)
  }

  function close() {
    reset()
    onClose()
  }

  function submit() {
    setProblem(null)
    if (needsProjects && picked.length === 0) {
      setProblem(t('team.invite.needs_projects'))
      return
    }
    setVerifying(true)
  }

  async function save(stepUpToken: string) {
    if (!member) return
    setVerifying(false)
    try {
      await patchMember.mutateAsync({ userId: member.user_id, role, projects: needsProjects ? picked : [], stepUpToken })
      setSaved(true)
    } catch (cause) {
      // 验证被拒（令牌过期/刚被用过/绑的不是这个人）不当死句丢回表单——
      // 站在这儿的人刚刚验过一次，再读一遍「得再验一次」是句死话。
      // 重开验证框让他再走一遍，才是那句话真正指的路。
      if (cause instanceof ApiFailure && cause.kind === 'needs_passkey') {
        setVerifying(true)
        return
      }
      setProblem(cause instanceof ApiFailure ? cause.message : t('error.generic'))
    }
  }

  if (!member) return null

  const who = member.name ?? member.email
  // 二次验证的资源串：服务端按路径里的 userId 取（auth.PathResource）。
  const resource = member.user_id

  return (
    <>
      <Modal
        className="team-theme"
        open={open && !verifying}
        onOpenChange={(next) => {
          if (!next) close()
        }}
        title={t('team.member.dialog.title', { name: who })}
      >
        {saved ? (
          <div data-testid="team-member-saved" className="flex flex-col gap-npc-gap">
            <p className="m-0 text-npc-body text-npc-ok-ink">
              <T id="team.member.saved" />
            </p>
            <div>
              <button
                type="button"
                data-testid="team-member-done"
                onClick={close}
                className="rounded-npc-button bg-npc-green px-[10px] py-[4px] text-npc-caption text-white"
              >
                <T id="team.invite.done" />
              </button>
            </div>
          </div>
        ) : (
          <form
            data-testid="team-member-form"
            className="flex flex-col gap-npc-gap"
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <label className="flex flex-col gap-[4px] text-npc-caption text-npc-ink-soft">
              <T id="team.member.dialog.role" />
              <select
                data-testid="team-member-role"
                value={role}
                onChange={(event) => setRole(event.target.value as Schemas['MemberRole'])}
                className="rounded-npc-button border border-npc-border-soft px-[10px] py-[6px] text-npc-body text-npc-ink"
              >
                {roles.map((item) => (
                  <option key={item.role} value={item.role}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            {/* 这一档到底是什么，就写在下面，别让他去别处对照。
                文字取自 `GET /team` 的 roles[].summary——服务端渲染好的那一份，
                前端不另写一版（同一个角色在两屏上出现两种说法，是这一屏
                包注释点名要避免的事）。 */}
            {roleSummary ? (
              <p className="m-0 -mt-[6px] text-npc-meta text-npc-ink-softer" data-testid="team-member-role-summary">
                {roleSummary}
              </p>
            ) : null}
            <ProjectPicker
              projects={projects}
              picked={picked}
              onChange={setPicked}
              ownerSeesAll={!needsProjects}
              testIdPrefix="team-member"
              legendId="team.member.dialog.projects"
              hintId="team.member.dialog.projects_hint"
            />
            {problem ? (
              <p className="m-0 text-npc-body-sm text-npc-red-ink" data-testid="team-member-problem">
                {problem}
              </p>
            ) : null}
            <div className="flex justify-end gap-npc-gap-sm">
              <button
                type="button"
                data-testid="team-member-cancel"
                onClick={close}
                className="rounded-npc-button border border-npc-border-soft px-[10px] py-[4px] text-npc-caption"
              >
                <T id="team.member.dialog.cancel" />
              </button>
              <button
                type="submit"
                data-testid="team-member-submit"
                disabled={patchMember.isPending}
                className="rounded-npc-button bg-npc-green px-[10px] py-[4px] text-npc-caption text-white disabled:opacity-50"
              >
                <T id="team.member.dialog.submit" />
              </button>
            </div>
          </form>
        )}
      </Modal>
      {verifying ? (
        <StepUpDialog
          open
          intent="change_team_permission"
          resource={resource}
          title={t('team.member.dialog.title', { name: who })}
          description={t('team.member.dialog.stepup')}
          onCancel={() => setVerifying(false)}
          onVerified={(token) => void save(token)}
        />
      ) : null}
    </>
  )
}
